import { useMemo } from 'react';

import { getFilterValues, useTableFilters } from '@/hooks/useTableFilters';
import type {
  EvaluationStatus,
  EvaluationWithSimulation,
  SimulationChannelType,
} from '@/modules/simulations/types';

import { categoryToFilterValue, filterValueToCategory } from './useSimulationsFilters';

export const useEvaluationsFilters = (evaluations: EvaluationWithSimulation[]) => {
  const { filters, setFilter, removeFilter, resetFilters } = useTableFilters({
    storageKey: 'evaluationsFilters',
  });

  // Hide category values not present in this run's data. Derived at render — we intentionally
  // don't touch localStorage so the user's intent survives navigating to a run where the
  // category is valid again.
  const effectiveFilters = useMemo(() => {
    const persisted = getFilterValues(filters, 'category');
    if (persisted.length === 0 || evaluations.length === 0) {
      return filters;
    }
    const valid = new Set(evaluations.map((e) => categoryToFilterValue(e.simulation.category)));
    const pruned = persisted.filter((v) => valid.has(v));
    if (pruned.length === persisted.length) {
      return filters;
    }
    const withoutCategory = filters.filter((f) => f.key !== 'category');
    return pruned.length === 0
      ? withoutCategory
      : [...withoutCategory, { key: 'category', values: pruned }];
  }, [filters, evaluations]);

  const activeFilterCount = effectiveFilters.length;

  const filteredEvaluations = useMemo(() => {
    if (!evaluations.length) {
      return [];
    }

    const statuses = getFilterValues(effectiveFilters, 'status') as EvaluationStatus[];
    const channels = getFilterValues(effectiveFilters, 'channel_type') as SimulationChannelType[];
    const categories = getFilterValues(effectiveFilters, 'category').map(filterValueToCategory);

    return evaluations.filter((e) => {
      if (statuses.length && !statuses.includes(e.status)) {
        return false;
      }
      if (channels.length && !channels.includes(e.simulation.channel_type)) {
        return false;
      }
      if (categories.length && !categories.includes(e.simulation.category)) {
        return false;
      }
      return true;
    });
  }, [evaluations, effectiveFilters]);

  return {
    filters: effectiveFilters,
    activeFilterCount,
    setFilter,
    removeFilter,
    resetFilters,
    filteredEvaluations,
  };
};
